import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import CommonLoader from '../../../../components/common-loader/CommonLoader';
import { ICONS } from '../../../../icons';
import Icon from '../../../../icons/Icon';
import sendRequest from '../../../../service/sendRequest';
import { fetchCache, imageCache, setInCache } from '../../../../utils/clearId8ImageCaches';
import { getConsumerId8Image, getId8Image } from '../../../../utils/id8ImagesRequests';
import { useProject } from '../../../../context/ProjectContext';
import { PROJECT_VARIANTS } from '../../../../constants';

import scss from './Id8Image.module.scss';

function Id8Image({ imageId, idea, className, onClick, disabled, style = {}, onImageLoaded }) {
  const { t } = useTranslation('id8');
  const [imageUrl, setImageUrl] = useState(null);
  const [hasError, setHasError] = useState(false);

  const { projectName, projectPassword } = useSelector((state) => state.consumer.id8);
  const { projectVariant = PROJECT_VARIANTS.ADMIN } = useProject();

  const handleClick = () => {
    if (onClick && !disabled) {
      onClick(imageId);
    }
  };

  const handleError = () => {
    setImageUrl(null);
    setHasError(true);
  };

  useEffect(() => {
    if (!imageId) return;

    if (imageCache.has(imageId)) {
      setImageUrl(imageCache.get(imageId));
      onImageLoaded?.(imageCache.get(imageId));
      return;
    }

    const cachedPromise = fetchCache.get(imageId);
    const fetchPromise =
      cachedPromise ||
      sendRequest(
        () => {
          if (projectVariant === PROJECT_VARIANTS.ADMIN) {
            return getId8Image(imageId);
          }

          if (projectVariant === PROJECT_VARIANTS.CONSUMER) {
            return getConsumerId8Image(imageId, { projectName, projectPassword });
          }
        },
        {
          onError: () => {
            fetchCache.delete(imageId);
            setHasError(true);
          },
        },
      ).then((response) => {
        const blobUrl = URL.createObjectURL(new Blob([response.data]));
        setInCache(imageCache, imageId, blobUrl);
        return blobUrl;
      });

    setInCache(fetchCache, imageId, fetchPromise);
    fetchPromise.then((url) => {
      setImageUrl(url);
      onImageLoaded?.(url);
    });
  }, [imageId, projectName, projectPassword, projectVariant, onImageLoaded]);

  useEffect(() => {
    return () => {
      if (imageUrl && !Array.from(imageCache.values()).includes(imageUrl)) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  if (!imageId) return null;
  if (hasError)
    return (
      <div className={scss.errorWrapper}>
        <Icon icon={ICONS.Logo} className={scss.errorIcon} />
        <span className={scss.errorText}>{t('errorOccurred')}</span>
      </div>
    );
  if (!imageUrl) return <CommonLoader isLoading={true} />;

  return (
    <img
      src={imageUrl}
      alt={idea ? `${idea} illustration` : 'Image'}
      className={className}
      onClick={handleClick}
      role='presentation'
      loading='lazy'
      onError={handleError}
      style={style}
    />
  );
}

export default Id8Image;
